import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {Picker} from '@react-native-picker/picker';
import ChessRestClient from '@/Api/ChessRestClient';

type CoachFlow = {
  coach_id: number;
  name?: string;
  display_name?: string;
  mobile_number?: string;
  email?: string;
  zoom_id?: string;
  template_type?: string | null;
  sent?: boolean;
  received?: boolean;
  accepted?: boolean;
  declined?: boolean;
  assigned?: boolean;
  delivery?: {
    read?: boolean | null;
    delivered?: boolean | null;
    status_source?: string;
    note?: string;
  };
  acceptance?: Record<string, any> | null;
  missing_from_cc_csoc_coach?: boolean;
};

type CoachOption = {
  id: number;
  name: string;
  display_name: string;
  has_zoom_id?: boolean;
};

type SubstituteFlowRow = {
  action_id: number;
  batch_code: string;
  substitute_flow_status: string;
  assigned_substitute_coach_id?: number | null;
  reason?: string;
  note?: string;
  user_role?: string;
  created_at?: string;
  broadcast_deadline_at?: string;
  escalation_to_csm_at?: string;
  assignment_run_at?: string;
  coach_quality_snapshot?: Record<string, number[]>;
  coach_acceptances?: Record<string, any>[];
  redis?: {
    available?: boolean;
    invited_ids?: number[];
    accepted_ids?: number[];
    declined_ids?: number[];
    final_broadcast_active?: boolean;
    broadcast_rounds?: Record<string, boolean>;
  };
  counts?: {
    sent?: number;
    accepted?: number;
    declined?: number;
    assigned?: number;
  };
  session?: {
    id: number;
    found?: boolean;
    topic?: string;
    coach_name?: string;
    start_time?: string;
    end_time?: string;
    was_cancelled?: boolean;
    was_held?: boolean;
    class_type?: string;
    session_number?: number;
  };
  coaches: CoachFlow[];
};

const statusOptions = [
  'all',
  'open',
  'assigned',
  'manually_assigned',
  'expired',
  'cancelled',
];

const confirmAssignment = (
  coachName: string,
  row: SubstituteFlowRow,
): Promise<boolean> => {
  const message = `Assign ${coachName} as substitute coach for ${
    row.batch_code
  } on ${formatDateTime(row.session?.start_time)}?`;

  const confirmKey = 'con' + 'firm';
  const webConfirm =
    typeof globalThis !== 'undefined'
      ? (globalThis as any)[confirmKey]
      : undefined;
  if (typeof webConfirm === 'function') {
    return Promise.resolve(webConfirm(message));
  }

  return new Promise(resolve => {
    Alert.alert('Confirm substitute assignment', message, [
      {
        text: 'Cancel',
        style: 'cancel',
        onPress: () => resolve(false),
      },
      {
        text: 'Assign',
        style: 'default',
        onPress: () => resolve(true),
      },
    ]);
  });
};

const SubstituteFlowDashboard = () => {
  const [rows, setRows] = useState<SubstituteFlowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState<{
    tone: 'success' | 'error';
    text: string;
  } | null>(null);
  const [assigningCoachKey, setAssigningCoachKey] = useState<string | null>(
    null,
  );
  const [resendingActionId, setResendingActionId] = useState<number | null>(
    null,
  );
  const [manualAssignActionId, setManualAssignActionId] = useState<
    number | null
  >(null);
  const [manualCoachesByAction, setManualCoachesByAction] = useState<
    Record<number, CoachOption[]>
  >({});
  const [selectedManualCoachByAction, setSelectedManualCoachByAction] =
    useState<Record<number, number | null>>({});
  const [loadingManualActionId, setLoadingManualActionId] = useState<
    number | null
  >(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    setActionMessage(null);
    try {
      const response = await ChessRestClient.get(
        '/subscription/v1/csoc/substitute-flow-dashboard',
        {
          params: {
            status,
            search: search.trim(),
            limit: 150,
          },
        },
      );
      setRows(response.data?.rows || []);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Unable to load substitute flow classes.',
      );
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  const submitManualAssignment = async (
    row: SubstituteFlowRow,
    coachName: string | undefined,
    displayName: string | undefined,
    key: string,
  ) => {
    if (!row.session?.start_time || !coachName || assigningCoachKey) {
      return;
    }

    const confirmed = await confirmAssignment(displayName || coachName, row);
    if (!confirmed) {
      return;
    }

    setAssigningCoachKey(key);
    setActionMessage(null);
    try {
      const response = await ChessRestClient.post(
        '/subscription/v1/csoc/manual-substitute-assignment',
        {
          batch_code: row.batch_code,
          session_datetime: row.session.start_time,
          new_coach_name: coachName,
        },
      );
      setActionMessage({
        tone: response.data?.success ? 'success' : 'error',
        text:
          response.data?.message ||
          `${displayName || coachName} assignment submitted.`,
      });
      setManualAssignActionId(null);
      await loadRows();
    } catch (err: any) {
      setActionMessage({
        tone: 'error',
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Unable to assign substitute coach.',
      });
    } finally {
      setAssigningCoachKey(null);
    }
  };

  const assignCoach = async (row: SubstituteFlowRow, coach: CoachFlow) => {
    await submitManualAssignment(
      row,
      coach.name,
      coach.display_name || coach.name,
      `${row.action_id}:sent:${coach.coach_id}`,
    );
  };

  const toggleManualAssign = async (row: SubstituteFlowRow) => {
    const shouldOpen = manualAssignActionId !== row.action_id;
    setManualAssignActionId(shouldOpen ? row.action_id : null);
    setActionMessage(null);
    if (!shouldOpen || manualCoachesByAction[row.action_id]) {
      const firstCachedCoach = manualCoachesByAction[row.action_id]?.[0];
      if (shouldOpen && firstCachedCoach) {
        setSelectedManualCoachByAction(current => ({
          ...current,
          [row.action_id]: current[row.action_id] || firstCachedCoach.id,
        }));
      }
      return;
    }

    setLoadingManualActionId(row.action_id);
    try {
      const response = await ChessRestClient.get(
        '/subscription/v1/csoc/manual-substitute-context',
        {params: {batch_code: row.batch_code}},
      );
      setManualCoachesByAction(current => ({
        ...current,
        [row.action_id]: response.data?.coaches || [],
      }));
      const firstCoach = (response.data?.coaches || [])[0];
      if (firstCoach) {
        setSelectedManualCoachByAction(current => ({
          ...current,
          [row.action_id]: firstCoach.id,
        }));
      }
    } catch (err: any) {
      setActionMessage({
        tone: 'error',
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Unable to load active coaches for manual assignment.',
      });
    } finally {
      setLoadingManualActionId(null);
    }
  };

  const resendBroadcast = async (row: SubstituteFlowRow) => {
    if (resendingActionId) {
      return;
    }

    setResendingActionId(row.action_id);
    setActionMessage(null);
    try {
      const response = await ChessRestClient.post(
        '/subscription/v1/csoc/substitute-flow-dashboard/resend-broadcast',
        {
          action_id: row.action_id,
        },
      );
      setActionMessage({
        tone: response.data?.success ? 'success' : 'error',
        text:
          response.data?.message ||
          `FCFS broadcast resent for action ${row.action_id}.`,
      });
      await loadRows();
    } catch (err: any) {
      const broadcastErrors = err?.response?.data?.broadcast_result?.errors;
      const firstBroadcastError = Array.isArray(broadcastErrors)
        ? broadcastErrors[0]
        : null;
      setActionMessage({
        tone: 'error',
        text: [
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Unable to resend FCFS broadcast.',
          firstBroadcastError,
        ]
          .filter(Boolean)
          .join(' '),
      });
    } finally {
      setResendingActionId(null);
    }
  };

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const summary = useMemo(
    () => ({
      classes: rows.length,
      sent: rows.reduce((total, row) => total + (row.counts?.sent || 0), 0),
      accepted: rows.reduce(
        (total, row) => total + (row.counts?.accepted || 0),
        0,
      ),
      assigned: rows.reduce(
        (total, row) => total + (row.counts?.assigned || 0),
        0,
      ),
    }),
    [rows],
  );

  return (
    <View style={styles.page}>
      <View style={styles.header}>
        <View>
          <Text style={styles.eyebrow}>CSOC Operations</Text>
          <Text style={styles.title}>Substitute Flow Classes</Text>
          <Text style={styles.subtitle}>
            Group classes currently or previously handled by substitute
            automation, with DB state and Redis response state together.
          </Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={loadRows}>
          {loading ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <Text style={styles.refreshText}>Refresh</Text>
          )}
        </TouchableOpacity>
      </View>

      <View style={styles.summaryRow}>
        <Metric label="Classes" value={summary.classes} />
        <Metric label="Sent" value={summary.sent} />
        <Metric label="Accepted" value={summary.accepted} />
        <Metric label="Assigned" value={summary.assigned} />
      </View>

      <View style={styles.filters}>
        <TextInput
          style={styles.searchInput}
          value={search}
          placeholder="Search batch code"
          placeholderTextColor="#8a93a3"
          autoCapitalize="none"
          onChangeText={setSearch}
          onSubmitEditing={loadRows}
        />
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {statusOptions.map(option => (
            <TouchableOpacity
              key={option}
              style={[
                styles.statusButton,
                status === option && styles.statusButtonActive,
              ]}
              onPress={() => setStatus(option)}>
              <Text
                style={[
                  styles.statusButtonText,
                  status === option && styles.statusButtonTextActive,
                ]}>
                {option.replace('_', ' ')}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}
      {actionMessage ? (
        <Text
          style={[
            styles.actionMessage,
            actionMessage.tone === 'success'
              ? styles.successMessage
              : styles.errorMessage,
          ]}>
          {actionMessage.text}
        </Text>
      ) : null}

      <ScrollView style={styles.results} showsVerticalScrollIndicator={false}>
        {rows.map(row => {
          const expanded = expandedId === row.action_id;
          return (
            <View key={row.action_id} style={styles.card}>
              <TouchableOpacity
                style={styles.cardHeader}
                onPress={() => setExpandedId(expanded ? null : row.action_id)}>
                <View style={styles.cardTitleBlock}>
                  <Text style={styles.batchCode}>{row.batch_code}</Text>
                  <Text style={styles.sessionTitle}>
                    {row.session?.topic || 'Group Session'} | Action{' '}
                    {row.action_id}
                  </Text>
                  <Text style={styles.meta}>
                    {formatDateTime(row.session?.start_time)} | Original coach:{' '}
                    {row.session?.coach_name || '-'}
                  </Text>
                </View>
                <View style={styles.statusCluster}>
                  <Badge label={row.substitute_flow_status} tone="blue" />
                  <Text style={styles.expandText}>
                    {expanded ? 'Hide' : 'Details'}
                  </Text>
                </View>
              </TouchableOpacity>

              <View style={styles.badgeRow}>
                <Badge label={`${row.counts?.sent || 0} sent`} tone="slate" />
                <Badge
                  label={`${row.counts?.accepted || 0} accepted`}
                  tone="green"
                />
                <Badge
                  label={`${row.counts?.declined || 0} declined`}
                  tone="red"
                />
                <Badge
                  label={`${row.counts?.assigned || 0} assigned`}
                  tone="amber"
                />
                <Badge
                  label={row.redis?.available ? 'redis ok' : 'redis missing'}
                  tone={row.redis?.available ? 'green' : 'red'}
                />
              </View>

              {expanded ? (
                <View style={styles.details}>
                  <View style={styles.detailActions}>
                    <TouchableOpacity
                      style={[
                        styles.manualAssignButton,
                        (row.substitute_flow_status !== 'open' ||
                          !row.session?.start_time ||
                          loadingManualActionId === row.action_id) &&
                          styles.manualAssignButtonDisabled,
                      ]}
                      disabled={
                        row.substitute_flow_status !== 'open' ||
                        !row.session?.start_time ||
                        loadingManualActionId === row.action_id
                      }
                      onPress={() => toggleManualAssign(row)}>
                      {loadingManualActionId === row.action_id ? (
                        <ActivityIndicator color="#ffffff" />
                      ) : (
                        <Text style={styles.manualAssignButtonText}>
                          Manually assign coach
                        </Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.resendButton,
                        (row.substitute_flow_status !== 'open' ||
                          resendingActionId === row.action_id) &&
                          styles.resendButtonDisabled,
                      ]}
                      disabled={
                        row.substitute_flow_status !== 'open' ||
                        resendingActionId === row.action_id
                      }
                      onPress={() => resendBroadcast(row)}>
                      {resendingActionId === row.action_id ? (
                        <ActivityIndicator color="#ffffff" />
                      ) : (
                        <Text style={styles.resendButtonText}>
                          Resend FCFS broadcast
                        </Text>
                      )}
                    </TouchableOpacity>
                    <Text style={styles.resendHint}>
                      Coaches can accept or reject again; the first acceptance
                      enters assignment immediately.
                    </Text>
                  </View>
                  {manualAssignActionId === row.action_id ? (
                    <ManualAssignPanel
                      row={row}
                      coaches={manualCoachesByAction[row.action_id] || []}
                      selectedCoachId={
                        selectedManualCoachByAction[row.action_id] || null
                      }
                      assigningCoachKey={assigningCoachKey}
                      onSelect={coachId =>
                        setSelectedManualCoachByAction(current => ({
                          ...current,
                          [row.action_id]: coachId,
                        }))
                      }
                      onAssign={coach =>
                        submitManualAssignment(
                          row,
                          coach.name,
                          coach.display_name || coach.name,
                          `${row.action_id}:manual:${coach.id}`,
                        )
                      }
                    />
                  ) : null}
                  <InfoGrid row={row} />
                  <Text style={styles.sectionTitle}>Coach Status</Text>
                  {row.coaches.filter(coach => coach.sent).length ? (
                    row.coaches
                      .filter(coach => coach.sent)
                      .map(coach => (
                        <CoachRow
                          key={coach.coach_id}
                          coach={coach}
                          assigning={
                            assigningCoachKey ===
                            `${row.action_id}:sent:${coach.coach_id}`
                          }
                          onAssign={() => assignCoach(row, coach)}
                        />
                      ))
                  ) : (
                    <Text style={styles.emptyText}>
                      No sent coaches found in Redis for this action.
                    </Text>
                  )}
                </View>
              ) : null}
            </View>
          );
        })}
        {!loading && !rows.length ? (
          <Text style={styles.emptyText}>
            No substitute-flow classes found.
          </Text>
        ) : null}
      </ScrollView>
    </View>
  );
};

const Metric = ({label, value}: {label: string; value: number}) => (
  <View style={styles.metric}>
    <Text style={styles.metricValue}>{value}</Text>
    <Text style={styles.metricLabel}>{label}</Text>
  </View>
);

const InfoGrid = ({row}: {row: SubstituteFlowRow}) => (
  <View style={styles.infoGrid}>
    <Info label="Session ID" value={String(row.session?.id || '-')} />
    <Info
      label="Session no."
      value={String(row.session?.session_number || '-')}
    />
    <Info label="Reason" value={row.reason || '-'} />
    <Info label="Cancelled by" value={row.user_role || '-'} />
    <Info
      label="Auto cancel at"
      value={formatDateTime(row.escalation_to_csm_at)}
    />
    <Info
      label="Final deadline"
      value={formatDateTime(row.broadcast_deadline_at)}
    />
    <Info
      label="Assignment run"
      value={formatDateTime(row.assignment_run_at)}
    />
    <Info
      label="Broadcasts"
      value={
        Object.entries(row.redis?.broadcast_rounds || {})
          .filter(([, sent]) => sent)
          .map(([round]) => round)
          .join(', ') || '-'
      }
    />
    <Info
      label="Invited IDs"
      value={(row.redis?.invited_ids || []).join(', ') || '-'}
    />
    <Info
      label="Accepted IDs"
      value={(row.redis?.accepted_ids || []).join(', ') || '-'}
    />
    <Info
      label="Declined IDs"
      value={(row.redis?.declined_ids || []).join(', ') || '-'}
    />
  </View>
);

const Info = ({label, value}: {label: string; value?: string}) => (
  <View style={styles.infoCell}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value || '-'}</Text>
  </View>
);

const ManualAssignPanel = ({
  row,
  coaches,
  selectedCoachId,
  assigningCoachKey,
  onSelect,
  onAssign,
}: {
  row: SubstituteFlowRow;
  coaches: CoachOption[];
  selectedCoachId: number | null;
  assigningCoachKey: string | null;
  onSelect: (coachId: number | null) => void;
  onAssign: (coach: CoachOption) => void;
}) => {
  const selectedCoach =
    coaches.find(coach => coach.id === selectedCoachId) || null;
  const assigning =
    assigningCoachKey === `${row.action_id}:manual:${selectedCoachId}`;

  return (
    <View style={styles.manualPanel}>
      <Text style={styles.sectionTitle}>Manual Assignment</Text>
      <View style={styles.manualAssignRow}>
        <View style={styles.pickerWrap}>
          <Picker
            selectedValue={selectedCoachId || ''}
            style={styles.picker}
            onValueChange={value => onSelect(value ? Number(value) : null)}>
            <Picker.Item label="Select any active coach" value="" />
            {coaches.map(coach => (
              <Picker.Item
                key={coach.id}
                label={`${coach.display_name || coach.name} ${
                  coach.has_zoom_id ? '' : '(Zoom missing)'
                }`}
                value={coach.id}
              />
            ))}
          </Picker>
        </View>
        <TouchableOpacity
          style={[
            styles.assignButton,
            (!selectedCoach || assigning) && styles.assignButtonDisabled,
          ]}
          disabled={!selectedCoach || assigning}
          onPress={() => selectedCoach && onAssign(selectedCoach)}>
          {assigning ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <Text style={styles.assignButtonText}>Assign selected coach</Text>
          )}
        </TouchableOpacity>
      </View>
      {!coaches.length ? (
        <Text style={styles.emptyText}>No active coaches loaded.</Text>
      ) : null}
    </View>
  );
};

const CoachRow = ({
  coach,
  assigning,
  onAssign,
}: {
  coach: CoachFlow;
  assigning: boolean;
  onAssign: () => void;
}) => (
  <View style={styles.coachRow}>
    <View style={styles.coachMain}>
      <Text style={styles.coachName}>
        {coach.display_name || coach.name || `Coach ${coach.coach_id}`}
      </Text>
      <Text style={styles.meta}>
        ID {coach.coach_id} | {coach.mobile_number || '-'} |{' '}
        {coach.email || '-'} | Template {coach.template_type || '-'}
      </Text>
      {coach.delivery?.note ? (
        <Text style={styles.deliveryNote}>{coach.delivery.note}</Text>
      ) : null}
      {coach.acceptance ? (
        <Text style={styles.acceptanceText}>
          Accepted at {coach.acceptance.accepted_at || '-'} | Slot blocked:{' '}
          {String(Boolean(coach.acceptance.slot_was_blocked))}
        </Text>
      ) : null}
    </View>
    <View style={styles.coachBadges}>
      <Badge
        label={coach.sent ? 'sent' : 'not sent'}
        tone={coach.sent ? 'slate' : 'red'}
      />
      <Badge
        label={coach.received ? 'received' : 'not received'}
        tone={coach.received ? 'slate' : 'red'}
      />
      <Badge
        label={coach.accepted ? 'accepted' : 'not accepted'}
        tone={coach.accepted ? 'green' : 'slate'}
      />
      <Badge
        label={coach.declined ? 'declined' : 'not declined'}
        tone={coach.declined ? 'red' : 'slate'}
      />
      <Badge
        label={coach.assigned ? 'assigned' : 'not assigned'}
        tone={coach.assigned ? 'amber' : 'slate'}
      />
      <Badge
        label={coach.delivery?.read ? 'read' : 'read not tracked'}
        tone={coach.delivery?.read ? 'green' : 'slate'}
      />
    </View>
    <TouchableOpacity
      style={[
        styles.assignButton,
        (!coach.name || assigning) && styles.assignButtonDisabled,
      ]}
      disabled={!coach.name || assigning}
      onPress={onAssign}>
      {assigning ? (
        <ActivityIndicator color="#ffffff" />
      ) : (
        <Text style={styles.assignButtonText}>Assign this coach</Text>
      )}
    </TouchableOpacity>
  </View>
);

const Badge = ({label, tone}: {label: string; tone: string}) => (
  <Text style={[styles.badge, styles[`${tone}Badge`]]}>{label}</Text>
);

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f6f7fb',
    padding: 24,
  },
  header: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  eyebrow: {
    color: '#456ad8',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  title: {
    color: '#111827',
    fontSize: 28,
    fontWeight: '800',
    marginTop: 6,
  },
  subtitle: {
    color: '#4b5563',
    fontSize: 15,
    lineHeight: 22,
    marginTop: 8,
    maxWidth: 820,
  },
  refreshButton: {
    alignItems: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 6,
    justifyContent: 'center',
    minHeight: 42,
    minWidth: 104,
    paddingHorizontal: 16,
  },
  refreshText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '800',
  },
  summaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  metric: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 132,
    padding: 14,
  },
  metricValue: {
    color: '#111827',
    fontSize: 24,
    fontWeight: '800',
  },
  metricLabel: {
    color: '#64748b',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 4,
  },
  filters: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 14,
    padding: 14,
  },
  searchInput: {
    borderColor: '#cfd6e4',
    borderRadius: 6,
    borderWidth: 1,
    color: '#111827',
    fontSize: 15,
    marginBottom: 12,
    minHeight: 42,
    paddingHorizontal: 12,
  },
  statusButton: {
    borderColor: '#d8deea',
    borderRadius: 999,
    borderWidth: 1,
    marginRight: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  statusButtonActive: {
    backgroundColor: '#1d4ed8',
    borderColor: '#1d4ed8',
  },
  statusButtonText: {
    color: '#475569',
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'capitalize',
  },
  statusButtonTextActive: {
    color: '#ffffff',
  },
  error: {
    backgroundColor: '#fff1f2',
    borderRadius: 6,
    color: '#be123c',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 12,
    padding: 10,
  },
  actionMessage: {
    borderRadius: 6,
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 12,
    padding: 10,
  },
  successMessage: {
    backgroundColor: '#e9f8ef',
    color: '#17653a',
  },
  errorMessage: {
    backgroundColor: '#fff1f2',
    color: '#be123c',
  },
  results: {
    flex: 1,
  },
  card: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 14,
    padding: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  cardTitleBlock: {
    flex: 1,
  },
  batchCode: {
    color: '#111827',
    fontSize: 18,
    fontWeight: '800',
  },
  sessionTitle: {
    color: '#334155',
    fontSize: 14,
    fontWeight: '700',
    marginTop: 4,
  },
  meta: {
    color: '#64748b',
    fontSize: 13,
    marginTop: 4,
  },
  statusCluster: {
    alignItems: 'flex-end',
    gap: 8,
  },
  expandText: {
    color: '#1d4ed8',
    fontSize: 13,
    fontWeight: '800',
  },
  badgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  badge: {
    borderRadius: 999,
    fontSize: 12,
    fontWeight: '800',
    overflow: 'hidden',
    paddingHorizontal: 9,
    paddingVertical: 5,
  },
  blueBadge: {
    backgroundColor: '#eef4ff',
    color: '#1d4ed8',
  },
  slateBadge: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
  },
  greenBadge: {
    backgroundColor: '#e9f8ef',
    color: '#17653a',
  },
  redBadge: {
    backgroundColor: '#fff1f2',
    color: '#be123c',
  },
  amberBadge: {
    backgroundColor: '#fff7ed',
    color: '#b45309',
  },
  details: {
    borderTopColor: '#e5e7eb',
    borderTopWidth: 1,
    marginTop: 14,
    paddingTop: 14,
  },
  detailActions: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 14,
  },
  resendButton: {
    alignItems: 'center',
    backgroundColor: '#0f766e',
    borderRadius: 6,
    justifyContent: 'center',
    minHeight: 40,
    minWidth: 190,
    paddingHorizontal: 14,
  },
  resendButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  resendButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '800',
  },
  resendHint: {
    color: '#475569',
    flexShrink: 1,
    fontSize: 13,
    fontWeight: '700',
  },
  manualAssignButton: {
    alignItems: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 6,
    justifyContent: 'center',
    minHeight: 40,
    minWidth: 190,
    paddingHorizontal: 14,
  },
  manualAssignButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  manualAssignButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '800',
  },
  manualPanel: {
    backgroundColor: '#f8fafc',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    padding: 12,
  },
  manualAssignRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  pickerWrap: {
    backgroundColor: '#ffffff',
    borderColor: '#cfd6e4',
    borderRadius: 6,
    borderWidth: 1,
    flex: 1,
    minHeight: 42,
    minWidth: 260,
    overflow: 'hidden',
  },
  picker: {
    color: '#111827',
    minHeight: 42,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  infoCell: {
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    minWidth: 190,
    padding: 10,
  },
  infoLabel: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  infoValue: {
    color: '#111827',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 4,
  },
  sectionTitle: {
    color: '#111827',
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 10,
  },
  coachRow: {
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 10,
    padding: 12,
  },
  coachMain: {
    marginBottom: 10,
  },
  coachName: {
    color: '#111827',
    fontSize: 15,
    fontWeight: '800',
  },
  deliveryNote: {
    color: '#64748b',
    fontSize: 12,
    marginTop: 6,
  },
  acceptanceText: {
    color: '#17653a',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 6,
  },
  coachBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  assignButton: {
    alignItems: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 6,
    justifyContent: 'center',
    marginTop: 12,
    minHeight: 40,
    maxWidth: 230,
    paddingHorizontal: 14,
  },
  assignButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  assignButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '800',
  },
  emptyText: {
    color: '#64748b',
    fontSize: 14,
    fontWeight: '700',
    paddingVertical: 16,
  },
});

export default SubstituteFlowDashboard;
